import React, { useState, useEffect } from 'react';
import { faMinus, faPlus, faTimes } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {toggleProduct, updateCateringMinimumSpend, clampManualQuantity} from '../redux/actions/rules';

// Exported (rather than kept inline in commitDraft below) so it can be unit
// tested without a React rendering harness, which this repo doesn't have —
// see tests/components/QuantityPicker.test.js. Returns the parsed integer, or
// null if the draft doesn't parse to a number at all (empty string,
// non-numeric garbage). A typed "0" parses to 0, not null — clampManualQuantity
// downstream is left to enforce minimum/maximum/floor rules on real numbers,
// this only guards against committing a store update for input that isn't a
// number at all (CR-186 review).
export const parseDraftQuantity = (draft) => {
  const parsed = parseInt(draft, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

function QuantityPicker({ item, quote, dispatch }) {

  // Free-text input commits on blur/Enter rather than on every keystroke, so
  // the value isn't fighting the user mid-typing while it gets clamped.
  const [draft, setDraft] = useState(item.quantity);

  useEffect(() => {
    setDraft(item.quantity);
  }, [item.quantity]);

  const commitQuantity = (requestedValue) => {
    const clamped = clampManualQuantity(item, quote.menu, quote.guests, requestedValue);
    dispatch({
      type: 'SET_QUANTITY',
      payload: { item: item, quantity: clamped, manual: true },
    });
    return clamped;
  };

  let stepProductQuantity = (type) => {
    let requestedValue = item.quantity;

    if (type === 'addition') {
      requestedValue++;
    } else {
      if ( item.quantity >= item.minimum_quantity) {
        requestedValue--;
      }
    }
    commitQuantity(requestedValue);
  };

  const handleDraftChange = (e) => {
    setDraft(e.target.value);
  };

  const commitDraft = () => {
    // An empty or non-numeric draft (the box cleared and blurred) is not the
    // customer choosing 0 — clampManualQuantity's own parseInt would otherwise
    // coerce it to a real, committed 0. Bail out and restore the box to the
    // current real quantity without touching the store; a genuinely typed "0"
    // still parses to 0, not null, and commits exactly as before (CR-186 review).
    const parsed = parseDraftQuantity(draft);
    if (parsed === null) {
      setDraft(item.quantity);
      return;
    }
    const clamped = commitQuantity(parsed);
    setDraft(clamped);
    dispatch(updateCateringMinimumSpend());
  };

  const handleBlur = () => {
    commitDraft();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitDraft();
    }
  };

  const removeProduct = (item) => {
    dispatch(toggleProduct(item));
  }
  return (
    <div className="QuantityPicker">
      {(item.quantity > item.minimum_quantity) &&
        <button type="button" className="item-btn" aria-label={`Decrease ${item.label} quantity`} onClick={() => stepProductQuantity('subtract')}>
          <FontAwesomeIcon icon={faMinus} />
        </button>
      }

      {(item.quantity === 0 || item.quantity >= item.minimum_quantity) &&
        <button type="button" className="item-btn" aria-label={`Remove ${item.label}`} onClick={() => removeProduct(item)}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      }
      <input
        type="text"
        inputMode="numeric"
        aria-label={`${item.label} quantity`}
        value={draft}
        className="product-quantity text-center float-start"
        onChange={handleDraftChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <button type="button" className="item-btn" aria-label={`Increase ${item.label} quantity`} onClick={() => stepProductQuantity('addition')}>
        <FontAwesomeIcon icon={faPlus} />
      </button>
    </div>
  );
}

QuantityPicker.propTypes = {
  item: PropTypes.object,
  quote: PropTypes.object,
  dispatch: PropTypes.func
}

const mapStateToProps = function (state) {
  return {
    quote: state.quote,
  };
};

export default connect(mapStateToProps)(QuantityPicker);
